import React, { useState, useEffect } from 'react';
import { Menu, X, LogOut, Moon, Sun, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * AppShell - Main app layout with sidebar navigation and content area.
 * @param {object} props
 * @param {string} props.appName - App display name for sidebar header
 * @param {React.ReactNode} [props.logo] - Small logo for sidebar header
 * @param {string} [props.brandColor] - Brand color name (orange, blue, etc.)
 * @param {Array} props.navItems - Navigation items: [{ key, label, icon: LucideIcon, component: React.Component, admin?: bool }]
 * @param {object} props.user - Current user object
 * @param {function} props.onLogout - Logout handler
 */
const AppShell = ({ appName, logo, brandColor = 'blue', navItems, user, api, onLogout }) => {
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#/', '').replace('#', '');
    if (hash && navItems.some(item => item.key === hash)) return hash;
    return navItems[0]?.key || '';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const visibleItems = navItems.filter(item => !item.admin || user?.role?.toLowerCase() === 'admin' || user?.role === 'Administrator');
  const ActiveComponent = visibleItems.find(item => item.key === activeTab)?.component;

  const brandAccent = {
    orange: 'bg-orange-600',
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    pink: 'bg-pink-600',
    purple: 'bg-purple-600',
  }[brandColor] || 'bg-blue-600';

  const brandActiveText = {
    orange: 'text-orange-600 dark:text-orange-400',
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    pink: 'text-pink-600 dark:text-pink-400',
    purple: 'text-purple-600 dark:text-purple-400',
  }[brandColor] || 'text-blue-600 dark:text-blue-400';

  const brandActiveBg = {
    orange: 'bg-orange-50 dark:bg-orange-900/20',
    blue: 'bg-blue-50 dark:bg-blue-900/20',
    green: 'bg-green-50 dark:bg-green-900/20',
    pink: 'bg-pink-50 dark:bg-pink-900/20',
    purple: 'bg-purple-50 dark:bg-purple-900/20',
  }[brandColor] || 'bg-blue-50 dark:bg-blue-900/20';

  // Sync hash ↔ active tab
  useEffect(() => {
    window.location.hash = `#/${activeTab}`;
  }, [activeTab]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#/', '').replace('#', '');
      if (hash && navItems.some(item => item.key === hash)) {
        setActiveTab(hash);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [navItems]);

  const handleNavClick = (key) => {
    setActiveTab(key);
    setSidebarOpen(false);
  };

  const SidebarContent = ({ isCollapsed = false }) => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
        {logo && <div className="flex-shrink-0">{logo}</div>}
        {!isCollapsed && <h1 className={`${brandActiveText} font-bold text-lg truncate`}>{appName}</h1>}
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {visibleItems.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.key;
          return (
            <button
              key={item.key}
              onClick={() => handleNavClick(item.key)}
              title={isCollapsed ? item.label : undefined}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-2.5 text-sm transition-colors ${
                isActive
                  ? `${brandActiveBg} ${brandActiveText} font-medium`
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span>{item.label}</span>}
              {!isCollapsed && isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-1">
        {/* User info */}
        {!isCollapsed && (
          <div className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 truncate">
            {user?.full_name || user?.username || 'User'}
            {(user?.role?.toLowerCase() === 'admin' || user?.role === 'Administrator') && (
              <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                admin
              </span>
            )}
          </div>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={isCollapsed ? (theme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-3'} py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors`}
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {!isCollapsed && <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
        </button>

        {/* Collapse toggle (desktop only) */}
        <button
          onClick={() => setCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-3'} py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors`}
        >
          {isCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          {!isCollapsed && <span>Collapse</span>}
        </button>

        {/* Logout */}
        <button
          onClick={onLogout}
          title={isCollapsed ? 'Sign out' : undefined}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-3'} py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors`}
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex md:flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-all duration-200 ${collapsed ? 'md:w-16' : 'md:w-72'}`}>
        <SidebarContent isCollapsed={collapsed} />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-72 bg-white dark:bg-gray-800 shadow-xl z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 dark:text-gray-300"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-semibold text-gray-900 dark:text-white truncate">{appName}</h1>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {ActiveComponent ? <ActiveComponent user={user} api={api} /> : (
            <div className="text-center text-gray-500 dark:text-gray-400 mt-20">
              Select a page from the sidebar
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
